


// mmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmm



import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { 
    getAllPendingLeaves, 
    getLeaveStats, 
    updateLeaveStatus 
} from "../../../redux/slices/adminLeaveSlice";
import { toast } from "react-toastify";

const LeavesPage = () => {
    const dispatch = useDispatch();
    
    const { 
        pendingLeaves, 
        stats,
        loading, 
        error, 
        success 
    } = useSelector((state) => state.adminLeave);
    
    const [search, setSearch] = useState("");
    const [showOnlyEarly, setShowOnlyEarly] = useState(false);
    const [selectedLeave, setSelectedLeave] = useState(null);
    const [remarks, setRemarks] = useState("");
    const [showActionModal, setShowActionModal] = useState(false);
    const [actionType, setActionType] = useState("");

    // ✅ Fetch data on mount
    useEffect(() => {
        fetchData();
    }, []);

    // ✅ Handle messages
    useEffect(() => {
        if (success) {
            toast.success("✅ Action completed successfully!");
            fetchData();
        }
        if (error) {
            toast.error(`❌ ${error}`);
        }
    }, [success, error]);

    // ✅ Fetch all data
    const fetchData = async () => {
        try {
            await Promise.all([
                dispatch(getAllPendingLeaves()),
                dispatch(getLeaveStats())
            ]);
        } catch (error) {
            toast.error("Failed to load data");
        }
    };

    // ✅ Handle approve/reject
    const handleAction = (leave, type) => {
        setSelectedLeave(leave);
        setActionType(type);
        setRemarks("");
        setShowActionModal(true);
    };

    const confirmAction = async () => {
        if (!selectedLeave) return;
        
        try {
            await dispatch(updateLeaveStatus({
                leaveId: selectedLeave._id,
                status: actionType,
                approvedBy: "Admin",
                remarks: remarks
            })).unwrap();
            
            setShowActionModal(false);
            setSelectedLeave(null);
            setRemarks("");
            toast.success(`Leave ${actionType} successfully!`);
        } catch (error) {
            toast.error(`Failed to ${actionType} leave`);
        }
    };

    // ✅ Filter leaves
    const filteredLeaves = pendingLeaves.filter(leave => {
        // Search filter
        const matchesSearch = leave.employeeName.toLowerCase().includes(search.toLowerCase()) ||
                             leave.employeeId.toLowerCase().includes(search.toLowerCase());
        
        // Early leave filter
        const matchesEarly = !showOnlyEarly || leave.isEarlyLeave;
        
        return matchesSearch && matchesEarly;
    });

    // ✅ Format date
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    // ✅ Get leave type label
    const getLeaveTypeLabel = (type) => {
        const labels = {
            'casual': 'Casual Leave',
            'sick': 'Sick Leave',
            'birthday': 'Birthday Leave',
            'early': 'Early Leave'
        };
        return labels[type] || type;
    };

    // ✅ Loading state
    if (loading && pendingLeaves.length === 0) {
        return (
            <div style={{ 
                display: "flex", 
                justifyContent: "center", 
                alignItems: "center", 
                height: "400px",
                color: "white" 
            }}>
                <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "40px", marginBottom: "20px" }}>⏳</div>
                    <p>Loading leave requests...</p>
                </div>
            </div>
        );
    }

    return (
        <div style={{ color: "white", position: "relative" }}>
            
            {/* HEADER */}
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div>
                    <h2 style={{ marginBottom:"4px" }}>📝 Leave Management</h2>
                    <p style={{ color:"#9ca3af", fontSize:"13px" }}>
                        Manage employee leave requests {showOnlyEarly && "(Early Leaves Only)"}
                        {pendingLeaves.length > 0 && ` • ${pendingLeaves.length} pending leaves`}
                    </p>
                </div>
            </div>

            {/* STATS CARDS */}
            <div style={{
                display:"grid",
                gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",
                gap:"16px",
                margin:"18px 0"
            }}>
                <Card icon="📝" title="Total Requests" value={stats.total || 0} color="#374151"/>
                <Card icon="✅" title="Approved" value={stats.approved || 0} left="#10b981"/>
                <Card icon="⛔" title="Rejected" value={stats.rejected || 0} left="#ef4444"/>
                <Card icon="⏳" title="Pending" value={stats.pending || 0} left="#3b82f6"/>
                <Card icon="🚨" title="Early Leaves" value={stats.earlyLeaves?.total || 0} left="#f59e0b"/>
            </div>

            {/* FILTER BAR */}
            <div style={{ 
                display:"flex", 
                justifyContent:"space-between", 
                alignItems:"center",
                marginBottom: "15px" 
            }}>
                <h3>📋 Pending Leave Requests</h3>

                <div style={{ display:"flex", gap:"10px", alignItems:"center" }}>
                    {/* Early Leave Filter */}
                    <label style={{ display:"flex", alignItems:"center", gap:"5px", cursor:"pointer" }}>
                        <input
                            type="checkbox"
                            checked={showOnlyEarly}
                            onChange={(e) => setShowOnlyEarly(e.target.checked)}
                            style={{ width: "16px", height: "16px", accentColor: "#f59e0b" }}
                        />
                        <span style={{ fontSize: "14px", color: "#f59e0b" }}>
                            Show Early Leaves Only
                        </span>
                    </label>

                    <input
                        placeholder="Search employee..."
                        style={input}
                        value={search}
                        onChange={(e)=>setSearch(e.target.value)}
                    />

                    <button
                        onClick={fetchData}
                        style={{
                            background: "#3b82f6",
                            border: "none",
                            padding: "10px 15px",
                            borderRadius: "6px",
                            color: "white",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "5px"
                        }}
                    >
                        🔄 Refresh
                    </button>
                </div>
            </div>

            {/* TABLE */}
            <div style={{ 
                background: "#111827", 
                borderRadius: "8px", 
                overflow: "hidden",
                border: "1px solid #374151",
                position: showActionModal ? "relative" : "static"
            }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    <thead>
                        <tr>
                            <Th>Employee</Th>
                            <Th>Leave Type</Th>
                            <Th>Date</Th>
                            <Th>Duration</Th>
                            <Th>Reason</Th>
                            <Th>Applied On</Th>
                            <Th>Actions</Th>
                        </tr>
                    </thead>

                    <tbody>
                        {filteredLeaves.map(leave => (
                            <tr key={leave._id} style={leave.isEarlyLeave ? { background: "rgba(254, 243, 199, 0.1)" } : {}}>
                                <Td>
                                    <div>
                                        <div style={{ color:"white", fontWeight:"500" }}>
                                            {leave.employeeName}
                                        </div>
                                        <div style={{ fontSize:"12px", color:"#9ca3af" }}>
                                            ID: {leave.employeeId}
                                        </div>
                                    </div>
                                </Td>
                                
                                <Td>
                                    <div>
                                        {getLeaveTypeLabel(leave.leaveType)}
                                        {leave.isEarlyLeave && (
                                            <span style={earlyLeaveBadge}>🚨 Early</span>
                                        )}
                                        {leave.isEarlyLeave && leave.earlyLeaveTime && (
                                            <div style={{ fontSize:"11px", color:"#f59e0b", marginTop:"2px" }}>
                                                ⏰ Time: {leave.earlyLeaveTime}
                                            </div>
                                        )}
                                    </div>
                                </Td>
                                
                                <Td>
                                    <div>
                                        {formatDate(leave.fromDate)}
                                        {leave.fromDate !== leave.toDate && (
                                            <div style={{ fontSize:"11px", color:"#9ca3af" }}>
                                                to {formatDate(leave.toDate)}
                                            </div>
                                        )}
                                    </div>
                                </Td>
                                
                                <Td>
                                    <div style={{ fontWeight:"bold" }}>
                                        {leave.days} {leave.days === 1 ? 'day' : 'days'}
                                    </div>
                                </Td>

                                <Td>
                                    <div style={{ maxWidth: "200px" }}>
                                        {leave.reason}
                                        {leave.isEarlyLeave && (
                                            <div style={{ fontSize:"11px", color:"#f59e0b", marginTop:"2px" }}>
                                                {leave.earlyLeaveReason}
                                            </div>
                                        )}
                                    </div>
                                </Td>

                                <Td>
                                    {new Date(leave.createdAt).toLocaleDateString('en-IN')}
                                </Td>

                                <Td>
                                    <div style={{ display:"flex", gap:"8px" }}>
                                        <button 
                                            style={btnGreen} 
                                            onClick={() => handleAction(leave, "approved")}
                                            title="Approve Leave"
                                            disabled={loading}
                                        >
                                            ✅ Approve
                                        </button>
                                        
                                        <button 
                                            style={btnYellow} 
                                            onClick={() => handleAction(leave, "rejected")}
                                            title="Reject Leave"
                                            disabled={loading}
                                        >
                                            ❌ Reject
                                        </button>
                                        
                                        <button 
                                            style={btnView} 
                                            onClick={() => {
                                                setSelectedLeave(leave);
                                                toast.info(
                                                    <div>
                                                        <strong>Leave Details:</strong><br/>
                                                        Employee: {leave.employeeName}<br/>
                                                        Type: {getLeaveTypeLabel(leave.leaveType)}<br/>
                                                        Date: {formatDate(leave.fromDate)}<br/>
                                                        Reason: {leave.reason}
                                                    </div>
                                                );
                                            }}
                                            title="View Details"
                                        >
                                            👁️ View
                                        </button>
                                    </div>
                                </Td>
                            </tr>
                        ))}

                        {filteredLeaves.length === 0 && (
                            <tr>
                                <td colSpan={7} style={{ textAlign:"center", padding:"40px" }}>
                                    {showOnlyEarly ? "🚫 No Early Leave Requests Found" : "✅ No Pending Leave Requests"}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* ✅ CUSTOM ACTION MODAL (Without Modal component) */}
            {showActionModal && selectedLeave && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: '#1f2937',
                        borderRadius: '12px',
                        width: '500px',
                        maxWidth: '90vw',
                        border: '1px solid #374151',
                        color: 'white'
                    }}>
                        {/* Modal Header */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '20px 24px',
                            borderBottom: '1px solid #374151'
                        }}>
                            <h3 style={{ margin: 0, fontSize: '18px' }}>
                                {actionType === 'approved' ? '✅ Approve' : '❌ Reject'} Leave Request
                            </h3>
                            <button
                                onClick={() => {
                                    setShowActionModal(false);
                                    setSelectedLeave(null);
                                    setRemarks("");
                                }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#9ca3af',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    width: '30px',
                                    height: '30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '4px'
                                }}
                                onMouseOver={(e) => e.target.style.backgroundColor = '#374151'}
                                onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                            >
                                &times;
                            </button>
                        </div>
                        
                        {/* Modal Body */}
                        <div style={{ padding: '24px' }}>
                            <p style={{ marginBottom: '20px' }}>
                                Are you sure you want to <strong>{actionType}</strong> this leave request?
                            </p>
                            
                            {/* Leave Details */}
                            <div style={{ 
                                background: "#111827", 
                                padding: "15px", 
                                borderRadius: "8px", 
                                marginBottom: "20px",
                                border: "1px solid #374151"
                            }}>
                                <p><strong>👤 Employee:</strong> {selectedLeave.employeeName}</p>
                                <p><strong>🆔 ID:</strong> {selectedLeave.employeeId}</p>
                                <p><strong>📅 Leave Type:</strong> {getLeaveTypeLabel(selectedLeave.leaveType)}</p>
                                <p><strong>📅 Date:</strong> {formatDate(selectedLeave.fromDate)}</p>
                                <p><strong>⏰ Duration:</strong> {selectedLeave.days} day(s)</p>
                                <p><strong>📝 Reason:</strong> {selectedLeave.reason}</p>
                                
                                {selectedLeave.isEarlyLeave && selectedLeave.earlyLeaveTime && (
                                    <p><strong>⏰ Early Leave Time:</strong> {selectedLeave.earlyLeaveTime}</p>
                                )}
                            </div>
                            
                            {/* Remarks Input */}
                            <div style={{ marginBottom: "25px" }}>
                                <label style={{ 
                                    display: "block", 
                                    marginBottom: "8px",
                                    fontWeight: "500"
                                }}>
                                    Remarks (Optional):
                                </label>
                                <textarea
                                    value={remarks}
                                    onChange={(e) => setRemarks(e.target.value)}
                                    placeholder="Add remarks if any..."
                                    rows="3"
                                    style={{
                                        width: "100%",
                                        padding: "12px",
                                        background: "#111827",
                                        border: "1px solid #374151",
                                        borderRadius: "6px",
                                        color: "white",
                                        resize: "vertical",
                                        fontSize: "14px"
                                    }}
                                />
                            </div>
                            
                            {/* Action Buttons */}
                            <div style={{ 
                                display: "flex", 
                                justifyContent: "flex-end", 
                                gap: "12px" 
                            }}>
                                <button
                                    style={{
                                        background: "#6b7280",
                                        color: "white",
                                        border: "none",
                                        padding: "10px 20px",
                                        borderRadius: "6px",
                                        cursor: "pointer",
                                        fontWeight: "500"
                                    }}
                                    onClick={() => {
                                        setShowActionModal(false);
                                        setSelectedLeave(null);
                                        setRemarks("");
                                    }}
                                    disabled={loading}
                                >
                                    Cancel
                                </button>
                                <button
                                    style={{
                                        background: actionType === 'approved' ? "#10b981" : "#ef4444",
                                        color: "white",
                                        border: "none",
                                        padding: "10px 20px",
                                        borderRadius: "6px",
                                        cursor: "pointer",
                                        fontWeight: "500"
                                    }}
                                    onClick={confirmAction}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <div className="btn-spinner"></div>
                                            Processing...
                                        </span>
                                    ) : `Confirm ${actionType}`}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ✅ Add CSS for spinner */}
            <style jsx>{`
                .btn-spinner {
                    border: 2px solid rgba(255, 255, 255, 0.3);
                    border-radius: 50%;
                    border-top: 2px solid white;
                    width: 16px;
                    height: 16px;
                    animation: spin 1s linear infinite;
                }
                
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

/* ---------- Reusable Components ---------- */
const Card = ({ icon, title, value, left }) => (
    <div style={{
        background:"#1e293b",
        border:"1px solid #374151",
        borderRadius:"8px",
        padding:"14px",
        borderLeft: left ? `4px solid ${left}` : "4px solid #6b7280"
    }}>
        <div style={{ fontSize:"25px" }}>{icon}</div>
        <h3 style={{ margin: "8px 0", fontSize: "14px", color: "#d1d5db" }}>{title}</h3>
        <p style={{ fontSize:"28px", fontWeight:"bold", margin: "0" }}>{value}</p>
    </div>
);

const Th = ({children}) => (
    <th style={{
        textAlign:"left",
        padding:"15px",
        background:"#111827",
        borderBottom:"1px solid #374151",
        color:"#9ca3af",
        fontWeight:"500"
    }}>{children}</th>
);

const Td = ({children}) => (
    <td style={{
        padding:"15px",
        borderBottom:"1px solid #374151"
    }}>{children}</td>
);

const input = {
    background:"#111827",
    color:"white",
    border:"1px solid #374151",
    padding:"10px",
    borderRadius:"6px",
    fontSize: "14px",
    minWidth: "200px"
};

const btnGreen = { 
    background:"#10b981", 
    border:0, 
    padding:"8px 12px", 
    borderRadius:"6px", 
    cursor:"pointer",
    color: "white",
    fontSize: "12px",
    fontWeight: "500"
};

const btnYellow = { 
    background:"#f59e0b", 
    border:0, 
    padding:"8px 12px", 
    borderRadius:"6px", 
    cursor:"pointer",
    color: "white",
    fontSize: "12px",
    fontWeight: "500"
};

const btnView = { 
    background:"#8b5cf6", 
    border:0, 
    padding:"8px 12px", 
    borderRadius:"6px", 
    cursor:"pointer",
    color: "white",
    fontSize: "12px",
    fontWeight: "500"
};

const earlyLeaveBadge = {
    background: "#fef3c7",
    color: "#92400e",
    padding: "2px 8px",
    borderRadius: "12px",
    fontSize: "10px",
    marginLeft: "6px",
    border: "1px solid #fbbf24"
};

export default LeavesPage;